// ─── Orderable product catalogue ─────────────────────────────────────────────
//
// Every product a member can be sent, whatever program it belongs to. The order
// tables are already program-agnostic (a CO monitor or a breathalyzer order is
// the same rows with a different sku), so the catalogue is too: a new orderable
// product is an entry here, not a new endpoint family.
//
// `kind` groups products that share fulfilment and entitlement rules; a kind's
// rule is looked up per kind, so adding a kind is a deliberate, type-checked
// step rather than a free-typed string. `programKeys` lists the programs a
// product may be ordered under — the staff order route refuses a sku whose
// program does not match the one the order is placed in.
//
// `displayName` is staff-facing product data, never member copy: a member
// surface keys its own localized label off the sku (ADR-017).
//
// Narrower views (the NRT catalogue) are derived from this list, never written
// beside it, so the two cannot drift.

/** The product families the catalogue holds. Additive: a new family is a new value. */
export const ORDER_PRODUCT_KIND = ['nrt', 'test_kit'] as const;
export type OrderProductKind = (typeof ORDER_PRODUCT_KIND)[number];

interface OrderProductShape {
  readonly sku: string;
  readonly kind: OrderProductKind;
  readonly programKeys: readonly [string, ...string[]];
  /** Physical form, for kinds that come in forms; `null` otherwise. */
  readonly form: string | null;
  /** Dose in milligrams, for dosed kinds; `null` otherwise. */
  readonly doseMg: number | null;
  readonly displayName: string;
}

// One tuple, shared by reference: `Object.freeze` on an inline array literal
// would widen it to `string[]`, losing the non-empty guarantee.
const NRT_PROGRAM_KEYS = Object.freeze(['tobacco'] as const);
const ALCOHOL_TEST_KIT_PROGRAM_KEYS = Object.freeze(['alcohol'] as const);
const DRUG_TEST_KIT_PROGRAM_KEYS = Object.freeze(['opioid', 'cannabis', 'stimulants'] as const);

/**
 * Every orderable product, in picklist order: NRT, then test kits. Within NRT:
 * gum before patch, gum by ascending dose, patch from the 21 mg starting dose
 * down the step-down.
 *
 * Test-kit skus are Pelago 1's, verbatim, so imported 1.0 shipment history and
 * new orders share one sku space. Pelago 1 sent the same UDS starter pack to
 * every drug program.
 */
export const ORDER_CATALOGUE = Object.freeze([
  Object.freeze({
    sku: 'NRT-GUM-2MG',
    kind: 'nrt',
    programKeys: NRT_PROGRAM_KEYS,
    form: 'gum',
    doseMg: 2,
    displayName: 'Nicotine gum 2 mg',
  }),
  Object.freeze({
    sku: 'NRT-GUM-4MG',
    kind: 'nrt',
    programKeys: NRT_PROGRAM_KEYS,
    form: 'gum',
    doseMg: 4,
    displayName: 'Nicotine gum 4 mg',
  }),
  Object.freeze({
    sku: 'NRT-PATCH-21MG',
    kind: 'nrt',
    programKeys: NRT_PROGRAM_KEYS,
    form: 'patch',
    doseMg: 21,
    displayName: 'Nicotine patch 21 mg',
  }),
  Object.freeze({
    sku: 'NRT-PATCH-14MG',
    kind: 'nrt',
    programKeys: NRT_PROGRAM_KEYS,
    form: 'patch',
    doseMg: 14,
    displayName: 'Nicotine patch 14 mg',
  }),
  Object.freeze({
    sku: 'NRT-PATCH-7MG',
    kind: 'nrt',
    programKeys: NRT_PROGRAM_KEYS,
    form: 'patch',
    doseMg: 7,
    displayName: 'Nicotine patch 7 mg',
  }),
  Object.freeze({
    sku: 'QG-ALC-STARTER',
    kind: 'test_kit',
    programKeys: ALCOHOL_TEST_KIT_PROGRAM_KEYS,
    form: null,
    doseMg: null,
    displayName: 'Breathalyzer',
  }),
  Object.freeze({
    sku: 'QG-OPD-STARTER',
    kind: 'test_kit',
    programKeys: DRUG_TEST_KIT_PROGRAM_KEYS,
    form: null,
    doseMg: null,
    displayName: 'UDS starter pack (1 urine cup)',
  }),
  Object.freeze({
    sku: 'QG-DRUGTESTCUP-16PANEL',
    kind: 'test_kit',
    programKeys: DRUG_TEST_KIT_PROGRAM_KEYS,
    form: null,
    doseMg: null,
    displayName: 'Urine cup (16-panel)',
  }),
] as const satisfies readonly OrderProductShape[]);

export type OrderCatalogueEntry = (typeof ORDER_CATALOGUE)[number];
export type OrderCatalogueSku = OrderCatalogueEntry['sku'];
/** The catalogue entries of one kind, with their literal types kept. */
export type OrderCatalogueEntryOfKind<K extends OrderProductKind> = Extract<
  OrderCatalogueEntry,
  { kind: K }
>;

/**
 * The SHAPE any orderable sku takes: an upper-case family head, then one to four
 * dash-separated upper-case alphanumeric segments. A strict superset of the NRT
 * shape (`NRT-{FORM}-{DOSE}MG`), so every NRT sku stays valid unchanged, while a
 * later device or test-kit sku needs no new pattern. Shape only — catalogue
 * membership is `isCatalogueSku`.
 */
export const ORDER_SKU_PATTERN = /^[A-Z][A-Z0-9]{1,15}(?:-[A-Z0-9]{1,16}){1,4}$/;

/** Does this sku take the generic orderable shape? */
export function isWellFormedOrderSku(sku: string): boolean {
  return ORDER_SKU_PATTERN.test(sku);
}

// Widened to `string` by annotation so lookups accept any caller input — a sku
// read off the wire is a string until this answers otherwise.
const ENTRIES_BY_SKU: ReadonlyMap<string, OrderCatalogueEntry> = new Map(
  ORDER_CATALOGUE.map((entry) => [entry.sku, entry]),
);

/** Is this sku an orderable product? Narrows to the catalogue's sku union. */
export function isCatalogueSku(sku: string): sku is OrderCatalogueSku {
  return ENTRIES_BY_SKU.has(sku);
}

/** The orderable product for a sku, or `undefined` when nothing by that sku exists. */
export function findOrderProduct(sku: string): OrderCatalogueEntry | undefined {
  return ENTRIES_BY_SKU.get(sku);
}

/** May this sku be ordered under this program? `false` for a sku outside the catalogue. */
export function isOrderableUnder(sku: string, programKey: string): boolean {
  const entry = ENTRIES_BY_SKU.get(sku);
  return entry !== undefined && entry.programKeys.some((key) => key === programKey);
}

/** Narrows a catalogue entry to one kind. */
export function isEntryOfKind<K extends OrderProductKind>(
  kind: K,
): (entry: OrderCatalogueEntry) => entry is OrderCatalogueEntryOfKind<K> {
  return (entry): entry is OrderCatalogueEntryOfKind<K> => entry.kind === kind;
}
